import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Contrast,
  DarkContrast,
  Saturation,
  SmartContrast,
} from "../../assets";
import { useSettings } from "../../context/SettingsContext";
import { Widget, AdvancedFunctionKey } from "../../types/widget";

import {
  AccessibilityAdjustableCard,
  AccessibilityClickableCard,
} from "../AccessibilityCards";
import { AccessibilityTooltipButton } from "../Buttons";
import ContrastModel from "./ContrastModelform";
import {
  postFeatureSelectMessage,
  postFeatureUnselectMessage,
} from "../../utils/message.helper";
import { Feature, Features } from "../../constants/features";
import { useKeyboardShortcuts } from "../../hooks";
import { publicAssetUrl } from "../../config";

const slidesData: {
  [key: string]: { text: Feature; image: string; label: string }[];
} = {
  [Features.COLOR_SATURATION]: [
    { text: Features.DEFAULT, image: publicAssetUrl("blur.png"), label: "default" },
    { text: Features.COLOR_HIGH_SATURATION, image: publicAssetUrl("blur.png"), label: "high_saturation" },
    { text: Features.COLOR_LOW_SATURATION, image: publicAssetUrl("blur.png"), label: "low_saturation" },
    { text: Features.COLOR_DESATURATE, image: publicAssetUrl("blur.png"), label: "desaturate" },
  ],
  [Features.COLOR_CONTRAST_MODE]: [
    { text: Features.DEFAULT, image: publicAssetUrl("contrast.png"), label: "default" },
    {
      text: Features.COLOR_LIGHT_CONTRAST,
      image: publicAssetUrl("contrast.png"),
      label: "light_contrast",
    },
    { text: Features.COLOR_DARK_CONTRAST, image: publicAssetUrl("contrast.png"), label: "dark_contrast" },
  ],
};

const allGridItems: { title: Feature | string, label: string, icon: string, settingKey: string }[] = [
  {
    title: Features.COLOR_SATURATION,
    label: "saturation",
    icon: publicAssetUrl("blur.png"),
    settingKey: "saturation",
  },
  {
    title: Features.COLOR_CONTRAST_MODE,
    label: "contrast_+",
    icon: publicAssetUrl("contrast.png"),
    settingKey: "contrast",
  },
  {
    title: Features.COLOR_CONTRAST_MODE,
    label: "contrast_mode",
    icon: publicAssetUrl("contrast.png"),
    settingKey: "contrastMode",
  },
  {
    title: Features.COLOR_SMART_CONTRAST,
    label: "smart_contrast",
    icon: publicAssetUrl("smallcontrast.png"),
    settingKey: "smartContrast",
  },
];

interface ColortAdjustmentWidgetProps {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const ColortAdjustmentWidget: React.FC<ColortAdjustmentWidgetProps> = ({
  setIsModalOpen,
}) => {
  const {
    setOversizeWidget,
    selectedCheckBox,
    setSelectedCheckBox,
    widgetButtonColor,
    widgetDataSettings,
  } = useSettings();

  const isMac = navigator.platform.toLowerCase().includes("mac");
  const modifierKey = isMac ? "Option" : "Alt";

  const [widgetData, setWidgetData] = useState<Widget | null>(() => {
    const stored = localStorage.getItem("widgetData");
    try {
      return stored && stored !== "undefined" ? JSON.parse(stored) : null;
    } catch (error) {
      console.error("Failed to parse widgetData from localStorage:", error);
      return null;
    }
  });
  const [selectedItems, setSelectedItems] = useState<Feature[]>(() => {
    const stored = localStorage.getItem("selectedItems");
    return stored ? JSON.parse(stored) : [];
  });

  const [isContrastModalOpen, setIsContrastModalOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState<{ [key: string]: number }>(
    {}
  );
  const buttonRefs = useRef<Map<Feature, HTMLButtonElement>>(new Map());

  const gridItems = useMemo(() => {
    if (!widgetDataSettings?.settings?.advanced?.functions) return allGridItems;
    return allGridItems.filter(
      (item) =>
        widgetDataSettings.settings?.advanced?.functions?.[
          item.settingKey as AdvancedFunctionKey
        ] === true
    );
  }, [widgetDataSettings]);

  const gridItemSettingKeys = useMemo(() => {
    return gridItems.map((item) => item.settingKey);
  }, [gridItems]);

  const getGridItem = useCallback((key: string) => {
    if (gridItemSettingKeys.includes(key)) {
      return gridItems.find((item) => item.settingKey === key);
    }    
    console.warn(`Grid item with key ${key} not found`);
    return null;
  }, [gridItems, gridItemSettingKeys]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.action === "load-widget") {
        const widgetData1 = event.data.data;
        if (!widgetData) {
          setWidgetData(widgetData1?.widget);
          localStorage.setItem(
            "widgetData",
            JSON.stringify(widgetData1?.widget)
          );
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [widgetData]);

  useEffect(() => {
    localStorage.setItem("selectedItems", JSON.stringify(selectedItems));
  }, [selectedItems]);

  useEffect(() => {
    const handleReset = (event: MessageEvent) => {
      if (event.data?.action === "RESET") {
        setSelectedItems([]);
        setCurrentSlide({});
        setIsContrastModalOpen(false);
        localStorage.removeItem("selectedItems");
        localStorage.removeItem("currentSlide");
      }
    };
    window.addEventListener("message", handleReset);
    return () => window.removeEventListener("message", handleReset);
  }, []);

  const handleItemClick = React.useCallback(
    (title: Feature) => {
      if (title === Features.COLOR_CONTRAST_MODE) {
        setIsContrastModalOpen(true);
      } else {
        const newSelectedItems = selectedItems.includes(title)
          ? selectedItems.filter((item) => item !== title)
          : [...selectedItems, title];

        if (selectedItems.includes(title)) {
          postFeatureUnselectMessage(title);
          if (title === Features.COLOR_SMART_CONTRAST) {
            setCurrentSlide((prev) => ({ ...prev, [title]: 0 }));
            localStorage.setItem(
              "currentSlide",
              JSON.stringify({ ...currentSlide, [title]: 0 })
            );
            if (selectedCheckBox.selected === Features.PROFILE_COLOR_BLIND) {
              setOversizeWidget(false);
            }
          }
        } else {
          postFeatureSelectMessage(title);
          if (title === Features.COLOR_SMART_CONTRAST) {
            setCurrentSlide((prev) => ({ ...prev, [title]: 1 }));
            localStorage.setItem(
              "currentSlide",
              JSON.stringify({ ...currentSlide, [title]: 1 })
            );
          }
        }

        setSelectedItems(newSelectedItems as Feature[]);
        localStorage.setItem("selectedItems", JSON.stringify(newSelectedItems));
      }
    },
    [selectedItems, selectedCheckBox, currentSlide, setOversizeWidget]
  );

  const handleSlideChange = (title: Feature, direction: "up" | "down") => {
    const slides = slidesData[title] || [];
    if (slides.length === 0) return;

    const currentIndex = currentSlide[title] || 0;
    let newIndex: number;
    if (direction === "up") {
      newIndex = (currentIndex + 1) % slides.length;
    } else {
      newIndex = currentIndex === 0 ? slides.length - 1 : currentIndex - 1;
    }

    setCurrentSlide((prev) => {
      const newCurrentSlide = { ...prev, [title]: newIndex };
      localStorage.setItem("currentSlide", JSON.stringify(newCurrentSlide));
      return newCurrentSlide;
    });

    const newSlide = slides[newIndex!];
    const isDefault = newSlide.label === "default";

    if (selectedCheckBox.selected === Features.PROFILE_LOW_VISION) {
      if (!isDefault) {
        setSelectedItems((prev) => {
          const filtered = prev.filter((item) => item !== title);
          const newSelectedItems = [...filtered, title];
          localStorage.setItem(
            "selectedItems",
            JSON.stringify(newSelectedItems)
          );
          return newSelectedItems;
        });
      }
    } else {
      setSelectedItems((prev) => {
        const newSelectedItems = isDefault
          ? prev.filter((item) => item !== title)
          : [...prev.filter((item) => item !== title), title];
        localStorage.setItem(
          "selectedItems",
          JSON.stringify(newSelectedItems)
        );
        return newSelectedItems as Feature[];
      });
    }

    if (isDefault) {
      postFeatureUnselectMessage(title);
    } else {
      postFeatureSelectMessage(newSlide.text);
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    title: Feature,
    isSlider: boolean = false
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      if (isSlider) {
        handleSlideChange(title, "up");
      } else {
        handleItemClick(title);
      }
    } else if (event.key === "Tab") {
      // If user Shift+Tabs from the first element inside this color group,
      // move focus to the last element of the content-adjustment group.
      if (event.shiftKey) {
        const allButtons = Array.from(buttonRefs.current.values());
        const firstButton = allButtons[0];
        if (firstButton && document.activeElement === firstButton) {
          const prevGroupLast = document.querySelector(
            '[data-group="content-adjustment"] button:last-of-type'
          ) as HTMLElement | null;
          if (prevGroupLast) {
            event.preventDefault();
            prevGroupLast.focus();
            return;
          }
        }
      }
    }
  };

  const setButtonRef = (title: Feature, element: HTMLButtonElement | null) => {
    if (element) {
      buttonRefs.current.set(title, element);
    } else {
      buttonRefs.current.delete(title);
    }
  };

  useEffect(() => {
    // Handle selections from profiles
    switch (selectedCheckBox.selected) {
      case Features.PROFILE_LOW_VISION: {
        const item = getGridItem("saturation");
        if (item && !selectedItems.includes(item.title as Feature)) {
          setSelectedItems((prev) => [...prev, item.title as Feature]);
          setCurrentSlide((prev) => ({ ...prev, [item.title as Feature]: 1 })); // 1: high saturation
          postFeatureSelectMessage(Features.COLOR_HIGH_SATURATION);
        }
        break;
      }
      case Features.PROFILE_SEIZURE_EPILEPTIC: {
        const item = getGridItem("saturation");
        if (item && !selectedItems.includes(item.title as Feature)) {
          setSelectedItems((prev) => [...prev, item.title as Feature]);
          setCurrentSlide((prev) => ({ ...prev, [item.title as Feature]: 2 })); // 2: low saturation
          postFeatureSelectMessage(Features.COLOR_LOW_SATURATION);
        }
        break;
      }
      case Features.PROFILE_COLOR_BLIND: {
        const item = getGridItem("smartContrast");
        if (item && !selectedItems.includes(item.title as Feature)) {
          setSelectedItems((prev) => [...prev, item.title as Feature]);
          setCurrentSlide((prev) => ({ ...prev, [item.title as Feature]: 1 })); // 1: on
          postFeatureSelectMessage(Features.COLOR_SMART_CONTRAST);
        }
        break;
      }
      default:
        break;
    }

    // Handle unselections from profiles
    if (selectedCheckBox.unSelected) {
      switch (selectedCheckBox.unSelected) {
        case Features.PROFILE_LOW_VISION: {
          const item = getGridItem("saturation");
          if (item && selectedItems.includes(item.title as Feature)) {
            setSelectedItems((prev) =>
              prev.filter((i) => i !== item.title)
            );
            setCurrentSlide((prev) => ({ ...prev, [item.title as Feature]: 0 }));
            postFeatureUnselectMessage(Features.COLOR_HIGH_SATURATION);
          }
          break;
        }
        case Features.PROFILE_SEIZURE_EPILEPTIC: {
          const item = getGridItem("saturation");
          if (item && selectedItems.includes(item.title as Feature)) {
            setSelectedItems((prev) =>
              prev.filter((i) => i !== item.title)
            );
            setCurrentSlide((prev) => ({ ...prev, [item.title as Feature]: 0 }));
            postFeatureUnselectMessage(Features.COLOR_LOW_SATURATION);
          }
          break;
        }
        case Features.PROFILE_COLOR_BLIND: {
          const item = getGridItem("smartContrast");
          if (item && selectedItems.includes(item.title as Feature)) {
            setSelectedItems((prev) =>
              prev.filter((i) => i !== item.title)
            );
            setCurrentSlide((prev) => ({ ...prev, [item.title as Feature]: 0 }));
            postFeatureUnselectMessage(Features.COLOR_SMART_CONTRAST);
          }
          break;
        }
        default:
          break;
      }

      // Reset the unselected flag to prevent re-triggering
      if (setSelectedCheckBox) {
        setSelectedCheckBox((prev) => ({ ...prev, unSelected: null }));
      }
    }
  }, [selectedCheckBox, selectedItems, getGridItem, setSelectedCheckBox]);

  useKeyboardShortcuts([
    {
      key: "y",
      altKey: true,
      ctrlKey: true,
      callback: (event) => {
        const saturationItem = getGridItem("saturation");
        if (saturationItem) {
          if (selectedItems.includes(saturationItem.title as Feature)) {
            const currentIndex = currentSlide[saturationItem.title] || 0;
            let nextIndex =
              (currentIndex + 1) % slidesData[saturationItem.title].length;

            if (event.shiftKey) {
              nextIndex =
                (currentIndex - 1 + slidesData[saturationItem.title].length) %
                slidesData[saturationItem.title].length;
            }

            setCurrentSlide((prev) => ({
              ...prev,
              [saturationItem.title]: nextIndex,
            }));
            localStorage.setItem(
              "currentSlide",
              JSON.stringify({
                ...currentSlide,
                [saturationItem.title]: nextIndex,
              })
            );

            const slideText = slidesData[saturationItem.title][nextIndex].text;

            if (nextIndex === 0) {
              setSelectedItems((prev) =>
                prev.filter((item) => item !== saturationItem.title)
              );
              postFeatureUnselectMessage(saturationItem.title as Feature);
            } else {
              postFeatureSelectMessage(slideText as Feature);
            }
          } else {
            setSelectedItems((prev) => [...prev, saturationItem.title as Feature]);

            const initialIndex = event.shiftKey
              ? slidesData[saturationItem.title].length - 1
              : 1;
            setCurrentSlide((prev) => ({
              ...prev,
              [saturationItem.title]: initialIndex,
            }));
            localStorage.setItem(
              "currentSlide",
              JSON.stringify({
                ...currentSlide,
                [saturationItem.title]: initialIndex,
              })
            );

            postFeatureSelectMessage(
              slidesData[saturationItem.title][initialIndex].text as Feature
            );
          }
        }
      },
    },
    {
      key: "x",
      altKey: true,
      ctrlKey: true,
      callback: () => {
        const contrastItem = getGridItem("contrast");
        if (contrastItem) handleItemClick(contrastItem.title as Feature);
      },
    },
    {
      key: "h",
      altKey: true,
      ctrlKey: true,
      callback: (event) => {
        const contrastModeItem = getGridItem("contrastMode");
        if (contrastModeItem) {
          if (selectedItems.includes(contrastModeItem.title as Feature)) {
            const currentIndex = currentSlide[contrastModeItem.title] || 0;
            let nextIndex =
              (currentIndex + 1) % slidesData[contrastModeItem.title].length;

            if (event.shiftKey) {
              nextIndex =
                (currentIndex - 1 + slidesData[contrastModeItem.title].length) %
                slidesData[contrastModeItem.title].length;
            }

            setCurrentSlide((prev) => ({
              ...prev,
              [contrastModeItem.title]: nextIndex,
            }));
            localStorage.setItem(
              "currentSlide",
              JSON.stringify({
                ...currentSlide,
                [contrastModeItem.title]: nextIndex,
              })
            );

            const slideText =
              slidesData[contrastModeItem.title][nextIndex].text;

            if (nextIndex === 0) {
              setSelectedItems((prev) =>
                prev.filter((item) => item !== contrastModeItem.title)
              );
              postFeatureUnselectMessage(contrastModeItem.title as Feature);
            } else {
              postFeatureSelectMessage(slideText as Feature);
            }
          } else {
            setSelectedItems((prev) => [...prev, contrastModeItem.title as Feature]);

            const initialIndex = event.shiftKey
              ? slidesData[contrastModeItem.title].length - 1
              : 1;
            setCurrentSlide((prev) => ({
              ...prev,
              [contrastModeItem.title]: initialIndex,
            }));
            localStorage.setItem(
              "currentSlide",
              JSON.stringify({
                ...currentSlide,
                [contrastModeItem.title]: initialIndex,
              })
            );

            postFeatureSelectMessage(
              slidesData[contrastModeItem.title][initialIndex].text as Feature
            );
          }
        }
      },
    },
    {
      key: "c",
      altKey: true,
      ctrlKey: true,
      callback: () => {
        const smartContrastItem = getGridItem("smartContrast");
        if (smartContrastItem)
          handleItemClick(smartContrastItem.title as Feature);
      },
    },
  ]);

  return (
    <>
      <div data-group="color-adjustment">
        <div className="p-2 bg-white rounded">
          <div className="grid grid-cols-1 gap-2">
            {/* Row 1 */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              {getGridItem("saturation") && (
                <AccessibilityAdjustableCard
                  item={{ ...getGridItem("saturation"), icon: <Saturation /> }}
                  selectedItems={selectedItems}
                  gridItems={gridItems}
                  currentSlide={currentSlide}
                  slidesData={slidesData}
                  setButtonRef={setButtonRef as (title: string, element: HTMLButtonElement | null) => void}
                  handleKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                    handleKeyDown(e, getGridItem("saturation")!.title as Feature, true)
                  }
                  handleSlideChange={handleSlideChange}
                  tooltip={
                    <AccessibilityTooltipButton
                      tooltipTitle="saturation"
                      tooltipDescription="changes_the_color_saturation"
                      tooltipAriaLabel="saturation_tooltip"
                      shortcutKey={`${modifierKey} + Ctrl + Y`}
                      widgetButtonColor={widgetButtonColor}
                    />
                  }
                />
              )}

              {getGridItem("contrast") && (
                <AccessibilityClickableCard
                  item={{ ...getGridItem("contrast"), icon: <Contrast /> }}
                  isSelected={selectedItems.includes(
                    getGridItem("contrast")!.title as Feature
                  )}
                  onClick={() =>
                    handleItemClick(getGridItem("contrast")!.title as Feature)
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                    handleKeyDown(e, getGridItem("contrast")!.title as Feature)
                  }
                  setButtonRef={setButtonRef as (title: string, element: HTMLButtonElement | null) => void}
                  tooltip={
                    <AccessibilityTooltipButton
                      tooltipTitle="contrast_mode"
                      tooltipDescription="adjust_text_and_background_colors_for_optimal_contrast"
                      tooltipAriaLabel="contrast_tooltip"
                      shortcutKey={`${modifierKey} + Ctrl + X`}
                      widgetButtonColor={widgetButtonColor}
                    />
                  }
                />
              )}
            </div>
            {/* Row 2 */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              {getGridItem("contrastMode") && (
                <AccessibilityAdjustableCard
                  item={{
                    ...getGridItem("contrastMode"),
                    icon: <DarkContrast />,
                  }}
                  selectedItems={selectedItems}
                  gridItems={gridItems}
                  currentSlide={currentSlide}
                  slidesData={slidesData}
                  setButtonRef={setButtonRef as (title: string, element: HTMLButtonElement | null) => void}
                  handleKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                    handleKeyDown(e, getGridItem("contrastMode")!.title as Feature, true)
                  }
                  handleSlideChange={handleSlideChange}
                  tooltip={
                    <AccessibilityTooltipButton
                      tooltipTitle="contrast_mode"
                      tooltipDescription="adjust_text_and_background_colors_for_optimal_contrast"
                      tooltipAriaLabel="contrast_mode_tooltip"
                      shortcutKey={`${modifierKey} + Ctrl + H`}
                      widgetButtonColor={widgetButtonColor}
                    />
                  }
                />
              )}

              {getGridItem("smartContrast") && (
                <AccessibilityClickableCard
                  item={{
                    ...getGridItem("smartContrast"),
                    icon: <SmartContrast />,
                  }}
                  isSelected={selectedItems.includes(
                    getGridItem("smartContrast")!.title as Feature
                  )}
                  onClick={() =>
                    handleItemClick(getGridItem("smartContrast")!.title as Feature)
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
                    handleKeyDown(e, getGridItem("smartContrast")!.title as Feature)
                  }
                  setButtonRef={setButtonRef as (title: string, element: HTMLButtonElement | null) => void}
                  tooltip={
                    <AccessibilityTooltipButton
                      tooltipTitle="smart_contrast"
                      tooltipDescription="changes_colors_to_make_them_more_visible"
                      tooltipAriaLabel="smart_contrast_tooltip"
                      tooltipAriaDescribedBy="smart-contrast-tooltip"
                      shortcutKey={`${modifierKey} + Ctrl + C`}
                      widgetButtonColor={widgetButtonColor}
                    />
                  }
                />
              )}
            </div>
          </div>
        </div>
        {isContrastModalOpen && (
          <ContrastModel onClose={() => setIsContrastModalOpen(false)}>
            <form>
              <div
                className="w-full h-10 py-2 px-5 flex items-center justify-between text-white"
                style={{ backgroundColor: widgetButtonColor }}
              >
                <p className="text-xs">Contrast</p>
                <button
                  onClick={() => setIsContrastModalOpen(false)}
                  className="text-white font-bold text-2xl cursor-pointer"
                >
                  ×
                </button>
              </div>
            </form>
          </ContrastModel>
        )}
      </div>
      <div className="h-5" />
    </>
  );
};

export default ColortAdjustmentWidget;
